import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Heart, MessageCircle, CornerUpLeft, UserPlus, AtSign, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Avatar, timeAgo } from "@/pages/Archive";

const TYPE = {
  like: { icon: Heart, verb: "liked your post", color: "text-blaze" },
  comment: { icon: MessageCircle, verb: "commented on your post", color: "text-forest" },
  reply: { icon: CornerUpLeft, verb: "replied to your comment", color: "text-forest" },
  comment_like: { icon: Heart, verb: "liked your comment", color: "text-blaze" },
  follow: { icon: UserPlus, verb: "started following you", color: "text-forest" },
  mention: { icon: AtSign, verb: "mentioned you", color: "text-blaze" },
};

export default function NotificationsPage() {
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!isAuthed) { setItems([]); return; }
    api.notifications().then((d) => {
      setItems((d.notifications || []).map((n) => ({ ...n })));
      if (d.unread) api.notificationsRead().then(() => setItems((prev) => prev.map((n) => ({ ...n, read: true })))).catch(() => {});
    }).catch(() => setItems([]));
  }, [isAuthed]);

  if (!isAuthed) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="notif-signin">
        <Bell size={30} className="text-blaze mx-auto" />
        <p className="font-serif text-3xl text-forest mt-4">Sign in to see your notifications.</p>
        <button onClick={() => openAuth("login")} className="mt-5 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="notif-signin-btn">Sign in</button>
      </div>
    </div>
  );

  return (
    <div className="bg-paper min-h-screen" data-testid="notifications-page">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-serif text-4xl text-charcoal tracking-tight">Notifications</h1>
        {items === null ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-saddle/30 rounded-md p-12 text-center mt-6" data-testid="notif-empty">
            <Bell size={26} className="text-saddle/50 mx-auto" />
            <p className="font-serif text-2xl text-forest mt-3">No notifications yet.</p>
            <p className="text-charcoal/60 mt-2">{`When hunters like, comment on, or follow you, it'll show up here.`}</p>
          </div>
        ) : (
          <ul className="mt-6 bg-white border border-saddle/20 rounded-md divide-y divide-saddle/12" data-testid="notif-list">
            {items.map((n, i) => {
              const t = TYPE[n.type] || { icon: Bell, verb: n.type, color: "text-forest" };
              const Icon = t.icon;
              const go = () => n.type === "follow" ? navigate(`/hunter/${n.actor_id}`) : navigate("/archive");
              return (
                <li key={n.id || i}>
                  <button onClick={go} className={`w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-paper transition-colors ${n.read ? "" : "bg-blaze/[0.04]"}`} data-testid={`notif-${i}`}>
                    <span className="relative shrink-0">
                      <Link to={`/hunter/${n.actor_id}`} onClick={(e) => e.stopPropagation()}><Avatar name={n.actor_name} size={40} /></Link>
                      <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white grid place-items-center ${t.color}`}><Icon size={12} className={n.type.includes("like") ? "fill-current" : ""} /></span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-charcoal leading-snug"><span className="font-semibold">{n.actor_name}</span> {t.verb}{n.text ? <span className="text-charcoal/55">{` — “${n.text}”`}</span> : ""}</p>
                      <p className="text-xs text-charcoal/40 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blaze shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
